import { fmt, pct, switchElectricityMode, type CalcResult, type Scenario } from "@h2lca/core";
import { Button, SegmentedControl, Select, TextArea } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { GlossaryTip } from "../GlossaryTip";
import { IssueLine } from "../IssueList";
import { NumField } from "../NumField";
import { Block } from "../Section";

type Mode = Scenario["electricity"]["mode"];

/** 전력 믹스 , 전력원별 비율 또는 사용량. 합성 계수는 오른쪽에 바로 보인다 */
export function ElectricityEditor({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const data = useMaster((s) => s.data);
  const update = useWorkshop((s) => s.update);
  const mix = scenario.electricity;
  const sources = data.sources.filter((s) => s.active);
  const issues = result.issues.filter((i) => i.step === "electricity");
  const setMix = (patch: Partial<Scenario["electricity"]>) => update((s) => ({ ...s, electricity: { ...s.electricity, ...patch } }));
  const setRow = (i: number, patch: Partial<Scenario["electricity"]["sources"][number]>) => setMix({ sources: mix.sources.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const unused = sources.find((s) => !mix.sources.some((r) => r.sourceId === s.id));
  const shareSum = mix.sources.reduce((a, r) => a + r.share, 0);

  return (
    <Block
      title={
        <>
          전력 믹스 <GlossaryTip k="emission_factor" />
        </>
      }
      aside={
        <SegmentedControl<Mode>
          label="입력 방식"
          value={mix.mode}
          options={[
            { value: "share", label: "비율 %" },
            { value: "kWh", label: "사용량 kWh" },
          ]}
          onChange={(mode) => setMix(switchElectricityMode(mix, mode, result.electricity.totalKWh))}
        />
      }
    >
      <p className="text-footnote text-ink-subtle">전력원별 사용량은 전력원의 비율을 구하는 데 사용합니다. 실제 배출량에는 경계 내 설비 전력량을 적용합니다. 비율에서 사용량으로 전환하면 설비 합계에 비례한 초깃값이 채워지며, 실측값으로 수정할 수 있습니다.</p>
      <ul className="flex flex-col gap-item">
        {mix.sources.map((row, i) => {
          const src = sources.find((s) => s.id === row.sourceId);
          const rr = result.electricity.sources.find((s) => s.sourceId === row.sourceId);
          return (
            <li key={`${row.sourceId}-${i}`} className="ws-electricity-row">
              <label className="control-field">
                <span className="control-label">전력원</span>
                <Select value={row.sourceId} onChange={(e) => setRow(i, { sourceId: e.target.value })}>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id} disabled={s.id !== row.sourceId && mix.sources.some((r) => r.sourceId === s.id)}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </label>
              {mix.mode === "share" ? (
                <NumField label="비율" unit="%" value={Math.round(row.share * 10000) / 100} allowNull={false} min={0} onChange={(v) => v !== null && setRow(i, { share: Math.min(1, Math.max(0, v / 100)) })} />
              ) : (
                <NumField label="사용량" unit="kWh" value={row.kWh ?? null} min={0} onChange={(v) => setRow(i, { kWh: v ?? 0 })} />
              )}
              <p className="font-mono text-footnote text-ink-muted sm:pb-item">
                계수 {rr ? fmt(rr.ef, 4) : "없음"} <span className="text-micro">kgCO2eq/kWh</span>
              </p>
              <div className="ws-inline-actions">
                {src && <InfoTip label={`${src.label} 설명`}>{src.description || src.label}{src.certRenewable && " 인증제 모의산정에서 재생에너지로 봅니다(교육용 단순화)."}</InfoTip>}
                <Button type="button" disabled={mix.sources.length <= 1} onClick={() => setMix({ sources: mix.sources.filter((_, j) => j !== i) })}>
                  제거
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-item">
        <Button variant="secondary" disabled={!unused} onClick={() => unused && setMix({ sources: [...mix.sources, { sourceId: unused.id, share: 0, kWh: 0 }] })}>
          전력원 추가
        </Button>
        {mix.mode === "share" && <span className={`text-footnote ${Math.abs(shareSum - 1) > 0.001 ? "text-tier-moderate" : "text-ink-subtle"}`}>합계 {pct(shareSum)}</span>}
      </div>
      <dl className="ws-kv border-t border-line-soft pt-item">
        <dt>합성 배출계수</dt>
        <dd>
          <b>{fmt(result.electricity.mixEf, 4)}</b> kgCO2eq/kWh
        </dd>
        <dt>경계 내 전력 사용량</dt>
        <dd>{fmt(result.electricity.totalKWh)} kWh</dd>
        {mix.mode === "kWh" && <><dt>전력원 입력 합계 / 설비 합계와의 차이</dt><dd>{fmt(result.electricity.enteredKWh)} / {fmt((result.electricity.enteredKWh ?? 0) - result.electricity.totalKWh)} kWh</dd></>}
      </dl>
      <TextArea label="전력량 측정기간·경계와 차이 근거" value={mix.note ?? ""} onChange={(e) => setMix({ note: e.target.value })} hint="예: 계량 기간, 공용 설비 포함 여부, 설비 합계와 차이가 나는 이유" />
      {scenario.method === "cert" && <p className="text-footnote text-ink-subtle">인증제의 재생에너지 인정조건은 교육용으로 단순화해, 재생에너지 전력의 배출계수를 0 으로 봅니다.</p>}
      {issues.length > 0 && (
        <div className="flex flex-col gap-label">
          {issues.map((i, k) => (
            <IssueLine key={`${i.code}-${k}`} issue={i} link={false} />
          ))}
        </div>
      )}
    </Block>
  );
}
